import React, { useRef, useState } from 'react';
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser';
import { makeStyles } from '@rentacenter/racstrap';
import {
  Modal,
  Backdrop,
  Fade,
  Button,
  Typography,
  Box,
  Paper,
} from '@material-ui/core';

import { QrScannerOutput } from '../../types/types';
import { processRmsEngine } from '../../api/purchaseOrders';
import { LoadingOverlay } from '../../common/LoadingOverlay/LoadingOverlay';

export const useStyles = makeStyles((theme: any) => ({
  videostyle: {
    width: '100%',
    height: theme.typography.pxToRem(240),
    borderRadius: 8,
    background: '#555555',
    objectFit: 'cover',
  },
  divvidstyle: {
    width: 360,
    maxWidth: '100%',
    margin: '12px auto',
  },
  paperstyle: {
    outline: 'none',
    width: 'min(92vw, 720px)',
    height: theme.typography.pxToRem(540),
    borderRadius: 12,
    overflow: 'hidden',
    margin: 'auto',
  },
  buttonLayoutStyle: {
    width: 'min(92vw, 180px)',
    overflow: 'hidden',
    margin: 'auto',
  },
  buttonstyle: {
    padding: '40px auto',
    margin: '40px auto',
    textTransform: 'none',
  },
  upcstyle: {
    marginTop: 10,
  },
  typostyle: {
    marginTop: 8,
    color: 'crimson',
  },
  titlestyle: {
    fontWeight: 500,
  },
}));
export interface QrScannerModalProps {
  onResolve: (out: QrScannerOutput | undefined) => void;
  onClose: () => void;
  open: boolean;
}

export const QrScannerModal = ({
  open,
  onClose,
  onResolve,
}: QrScannerModalProps) => {
  const classes = useStyles();
  const [qrCode, setQrCode] = useState<string>('');

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showLoader, setShowLoader] = useState<boolean>(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);

  const mapResult = (response: any) => {
    if (response?.code == '200') {
      onResolve(response);
    } else {
      onResolve(undefined);
    }
  };

  const searchRms = async () => {
    setShowLoader(true);
    const store = window.sessionStorage.getItem('storeNumber');
    const payload = {
      storeNumber: store,
      barcode: qrCode,
    };

    await processRmsEngine(payload)
      .then((out) => {
        mapResult(out);
      })
      .catch((ee) => {
        setErrorMessage(ee.message);
      })
      .finally(() => {
        setShowLoader(false);
      });
  };

  const skipNoise = (err: any): boolean => {
    if (err.name !== 'NotFoundException') {
      return false; // noise
    }

    const msg = err.message;

    if (msg) {
      let input = msg as string;
      input = input.trim().toLowerCase();
      if (input.includes('no multiforma')) {
        return false; // noise
      }
    }

    return true;
  };

  const startScanner = async () => {
    try {
      if (!videoRef.current) return;
      // ensure attributes for mobile autoplay
      videoRef.current.setAttribute('playsinline', 'true');
      videoRef.current.muted = true;
      videoRef.current.autoplay = true;

      // lazily create reader
      if (!readerRef.current)
        readerRef.current = new BrowserMultiFormatReader();

      const controls = await readerRef.current.decodeFromVideoDevice(
        undefined,
        videoRef.current,
        (result, err, newControls) => {
          if (!controlsRef.current) controlsRef.current = newControls;

          if (result) {
            setQrCode(result.getText());
            newControls.stop();
          } else if (err && skipNoise(err)) {
            setErrorMessage(`${err.name}: ${err.message}`);
          }
        }
      );

      controlsRef.current = controls;

      // Explicitly kick video playback (some browsers need this)
      try {
        await videoRef.current.play();
      } catch {
        /* ignore; user gesture already happened via Open button */
      }
    } catch (e) {
      const err = e as Error;
      setErrorMessage(err.message || 'Failed to start scanner');
    }
  };

  const stopScanner = () => {
    controlsRef.current?.stop();
    controlsRef.current = null;
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={() => onClose()}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 250 }}
        aria-labelledby="scanner-title"
      >
        <Fade
          in={open}
          onEntered={startScanner} // <-- start after transition fully entered
          onExited={stopScanner} // <-- stop as we begin to exit
        >
          <Paper elevation={8} className={classes.paperstyle}>
            {showLoader && <LoadingOverlay />}
            <Box
              display="flex"
              alignItems="center"
              p={1.5}
              borderBottom="1px solid #eee"
            >
              <Typography id="scanner-title" className={classes.titlestyle}>
                Scan QR / Barcode
              </Typography>
              <div className={classes.buttonLayoutStyle}>
                <Button
                  onClick={() => searchRms()}
                  color="primary"
                  variant="contained"
                  disabled={!qrCode}
                  className={classes.buttonstyle}
                >
                  Search UPC
                </Button>
              </div>
              <Button
                onClick={() => onClose()}
                color="primary"
                variant="contained"
              >
                Close
              </Button>
            </Box>

            <Box p={2} textAlign="center">
              {
                <Typography className={classes.upcstyle}>
                  UPC: <strong>{qrCode}</strong>
                </Typography>
              }
              {errorMessage && (
                <Typography className={classes.typostyle}>
                  {errorMessage}
                </Typography>
              )}
              <div className={classes.divvidstyle}>
                <video
                  ref={videoRef}
                  className={classes.videostyle}
                  autoPlay
                  muted
                  playsInline
                />
              </div>
            </Box>
          </Paper>
        </Fade>
      </Modal>
    </>
  );
};
